import React, { useEffect } from 'react';
import ScrollAnimation from 'react-animate-on-scroll';
import { Helmet } from 'react-helmet-async';
import { useMediaQuery } from 'react-responsive';
import { get, map } from 'lodash';
import FulltimeRecruitmentCard from 'components/company/FulltimeRecruitmentCard';
import ParttimeRecruitmentCard from 'components/company/ParttimeRecruitmentCard';
import { getCompanySearch, getCompanySearchList, getRecruitments } from 'libs/api/main';
import { useDataApi, usePagination } from 'libs/hooks';
import { TCompanyNewsletter, TCompanyPartner, TCompanySearch, TRecruitment } from 'modules/main';
import { TListResponse, TPagination } from 'modules/types';
import { CWindowWidth } from 'constants/base.const';
import { ERecruitmentCategory } from 'enums/base.enum';
import { List, Avatar, Col, Row, Tabs, Tag, Typography } from 'antd';
import styled from 'styled-components';
import { PrimaryColor, device } from 'GlobalStyles';
import BannerType04 from 'assets/images/banners/9.webp';
import 'animate.css/animate.min.css';

const { TabPane } = Tabs;
const { Text, Title } = Typography;
const CommonWrapper = styled.div`
  .title_wrapper {
    width: 100%;
    height: 300px;
    background: linear-gradient(0deg, rgba(42, 71, 83, 0.3), rgba(42, 71, 83, 0.3)), url(${BannerType04});
    background-size: cover;
    padding: 100px 10px;
    .ant-typography {
      width: 100%;
      max-width: ${CWindowWidth - 300}px;
      color: #fff;
      margin: auto;
      display: block;
    }
    span.ant-typography {
      font-size: 16px;
    }
  }
  .ant-typography a,
  .ant-typography {
    color: #000;
  }
`;

const CompanySearchWrapper = styled.div`
  .ant-tabs-nav-list {
    transform: translate(13px, 0px) !important;
  }

  .logo {
    width: 100%;
    max-width: ${CWindowWidth - 300}px;
    height: 160px;
    border-radius: 8px;
    text-align: center;
  }

  .partner_image {
    max-width: 160px;
    width: 100%;
    height: 160px;
    @media screen and ${device.md} {
      height: 140px;
    }
    background: #f9f9f9;
  }

  .newsletter_thumbnail {
    width: 90%;
    height: 120px;
    background: #f0f8f6;
    display: block;
    text-align: left;
  }

  .newsletter_text {
    height: 112px;
    line-height: 18px;
    text-align: justify;
    .title {
      margin-bottom: 10px;
      text-overflow: ellipsis;
      display: -webkit-box;
      -webkit-box-orient: vertical;
      -webkit-box-pack: center;
      -webkit-line-clamp: 1;
      word-break: break-word;
      white-space: normal;
      overflow: hidden;
    }
    .body {
      overflow: hidden;
      text-overflow: ellipsis;
      display: -webkit-box;
      -webkit-box-orient: vertical;
      -webkit-box-pack: center;
      -webkit-line-clamp: 4;
      word-break: break-word;
      white-space: pre-line;
    }
  }

  .ant-tag.news-tag {
    background-color: #f0f8f6 !important;
    border-radius: 8px !important;
    color: ${PrimaryColor} !important;
    border-color: transparent !important;
    margin-bottom: 6px;
    cursor: pointer;
  }
  .ant-pagination {
    text-align: left;
    margin: 16px 0px !important;
  }
  .ant-pagination-item {
    display: none;
  }
  .ant-tabs-nav-wrap {
    max-width: 800px;
    margin: auto;
    overflow: auto !important;
    -ms-overflow-style: none; /* IE and Edge */
    scrollbar-width: none; /* Firefox */
    &::-webkit-scrollbar {
      /* Chrome, Safari */
      display: none;
    }
  }
`;

const CompanySearch = () => {
  const [{ data, loading }] = useDataApi<TCompanySearch>(getCompanySearchList.bind(null));

  return (
    <CompanySearchWrapper>
      <Helmet>
        <title>회사 탐색</title>
      </Helmet>
      <CommonWrapper>
        <div className='title_wrapper'>
          <Title style={{ marginBottom: '10px' }}>회사 탐색</Title>
          <Text>Career Step이 제공하는 컨설팅 Firm 정보로 입사 준비를 시작하세요.</Text>
        </div>
        <Tabs centered={true}>
          {map(data, (v: TCompanySearch) => (
            <TabPane tab={get(v, 'company_name')} key={get(v, 'id')}>
              <TabContent id={get(v, 'id')} />
            </TabPane>
          ))}
        </Tabs>
      </CommonWrapper>
    </CompanySearchWrapper>
  );
};

function TabContent({ id }: { id: number }) {
  const [{ data, loading }] = useDataApi<TCompanySearch>(getCompanySearch.bind(null, id));
  const isMobile = useMediaQuery({ query: device.md });
  const defaultPadding = isMobile === true ? 0 : 8;

  const Partner = (partner: TCompanyPartner) => {
    const newslink = get(partner, 'newslink');
    const visibility = newslink ? true : false;

    return (
      <Col xs={8} sm={8} md={8} lg={8} xl={8} style={{ marginBottom: '32px', lineHeight: '22px' }}>
        <Avatar shape='square' className='partner_image' src={get(partner, 'profile_image')} />
        <div style={{ marginTop: '5px', fontSize: 'calc(0.2vw + 12px)' }}>{partner.fullname} 파트너</div>
        <div style={{ fontSize: '12px' }}>{partner.position}</div>
        <a href={newslink} target='_blank'>
          <Tag className='news-tag' visible={visibility}>
            관련 기사
          </Tag>
        </a>
      </Col>
    );
  };

  const Newsletter = (newsletter: TCompanyNewsletter) => {
    const thumbnail = get(newsletter, 'thumbnail');

    return (
      <a href={get(newsletter, 'newslink')} target='_blank'>
        <Row style={{ margin: '16px auto' }} justify='space-between'>
          <Col xs={24} sm={24} md={8} lg={8} xl={8}>
            <Avatar shape='square' className='newsletter_thumbnail hide-on-mobile' src={thumbnail} />
          </Col>
          <Col xs={24} sm={24} md={16} lg={16} xl={16}>
            <div className='newsletter_text'>
              <Title className='title' level={4}>
                {get(newsletter, 'title')}
              </Title>
              <Text className='body'>{get(newsletter, 'body')}</Text>
            </div>
          </Col>
        </Row>
      </a>
    );
  };

  return (
    <div style={{ minHeight: '600px' }}>
      <div style={{ textAlign: 'center' }}>
        {get(data, 'logo_image') && <Avatar shape='square' className='logo' src={get(data, 'logo_image')} />}
      </div>
      <div
        style={{
          margin: 'auto',
          maxWidth: `${CWindowWidth - 300}px`,
          padding: '0px 5px',
        }}
      >
        <Row gutter={[32, 8]} style={{ marginTop: '40px', marginBottom: '40px' }}>
          <Col xs={24} sm={24} md={8} lg={8} xl={8}>
            <Title level={2}>회사 소개</Title>
            <Text className='hide-on-mobile' style={{ marginTop: '8px' }}>
              온라인 오픈 소스를 기반으로 작성되었습니다.
            </Text>
          </Col>
          <Col xs={24} sm={24} md={16} lg={16} xl={16}>
            <Text>
              {get(data, 'introduction') && (
                <span
                  style={{
                    fontSize: '16px',
                    whiteSpace: 'pre-line',
                    wordBreak: 'break-word',
                  }}
                >
                  {get(data, 'introduction')}
                </span>
              )}
              <br />
              <br />
              {get(data, 'address') && (
                <span>
                  <strong>주소</strong>&nbsp;&nbsp;{get(data, 'address')}
                </span>
              )}
              <br />
              {get(data, 'website_url') && (
                <span>
                  <strong>공식 홈페이지</strong>&nbsp;&nbsp;
                  <a href={get(data, 'website_url')} target='_blank'>
                    {get(data, 'website_url')}
                  </a>
                </span>
              )}
              <br />
              {get(data, 'blog_url') && (
                <span>
                  <strong>오피스 블로그</strong>&nbsp;&nbsp;
                  <a href={get(data, 'blog_url')} target='_blank'>
                    {get(data, 'blog_url')}
                  </a>
                </span>
              )}
            </Text>
          </Col>
        </Row>
        {get(data, 'partners', []).length > 0 && (
          <Row gutter={[32, 8]} style={{ marginTop: '40px', marginBottom: '40px' }}>
            <Col xs={24} sm={24} md={8} lg={8} xl={8}>
              <Title level={2}>파트너 소개</Title>
              <Text style={{ marginTop: '8px' }} className='hide-on-mobile'>
                온라인 오픈 소스를 기반으로 작성되었습니다.
              </Text>
            </Col>
            <Col xs={24} sm={24} md={16} lg={16} xl={16}>
              <ScrollAnimation animateIn='fadeIn' animateOnce={true} duration={1.5}>
                <Row gutter={[8, 0]}>
                  {map(get(data, 'partners', []), (content: TCompanyPartner) => Partner(content))}
                </Row>
              </ScrollAnimation>
            </Col>
          </Row>
        )}
        {get(data, 'keyword_image') && (
          <Row gutter={[32, 8]} style={{ marginTop: '40px', marginBottom: '40px' }}>
            <Col xs={24} sm={24} md={8} lg={8} xl={8}>
              <Title level={2} style={{ marginBottom: '8px' }}>
                회사 핵심 키워드
              </Title>
              <Text>
                전현직 직원들에게 회사를 잘 설명하는 5가지 대표 키워드를 받았습니다.
                <br />
                *회사가 공식적으로 제공한 키워드는 아닙니다.
              </Text>
            </Col>
            <Col xs={24} sm={24} md={16} lg={16} xl={16}>
              <ScrollAnimation animateIn='flipInY' animateOnce={true} duration={1.5}>
                <Avatar
                  shape='square'
                  src={get(data, 'keyword_image')}
                  style={{ width: '100%', height: 'auto' }}
                />
              </ScrollAnimation>
            </Col>
          </Row>
        )}
      </div>
      {get(data, 'company_name') && <Recruitments companyName={get(data, 'company_name')} />}
      {get(data, 'news_letters', []).length > 0 && (
        <div style={{ margin: '32px auto', maxWidth: `${CWindowWidth - 300}px` }}>
          <Row style={{ width: '100%', padding: '0px' }} gutter={[4 * defaultPadding, defaultPadding]}>
            <Col xs={24} sm={24} md={8} lg={8} xl={8}>
              <Title level={2} style={{ marginBottom: '8px' }}>
                관련 뉴스
              </Title>
              <Text>
                회사와 관련해서 읽어볼만한 기사를 Career Step이 선정했습니다. 상시 업데이트 예정입니다.
              </Text>
            </Col>
            <Col xs={24} sm={24} md={16} lg={16} xl={16}>
              {map(get(data, 'news_letters'), (content: TCompanyNewsletter) => Newsletter(content))}
            </Col>
          </Row>
        </div>
      )}
    </div>
  );
}

const Recruitments = ({ companyName }: { companyName: string }) => {
  const [pagination, setPagination] = usePagination(4);
  const reloadPage = (page?: Partial<TPagination>) => {
    setPagination({
      ...pagination,
      ...page,
    });
  };
  const getPromise = getRecruitments.bind(null, '', {
    params: {
      current: pagination.current,
      pageSize: pagination.pageSize,
      q: companyName,
    },
  });
  const [{ data, loading }, setCallback] = useDataApi<TListResponse<TRecruitment>>(getPromise, {
    contents: [],
    last: false,
  });

  useEffect(() => {
    setCallback(() => getPromise);
  }, [pagination]);

  return (
    <>
      {get(data, 'contents', []).length > 0 && (
        <div style={{ background: '#F9F9F9', width: '100%', padding: '30px 0px' }}>
          <div style={{ maxWidth: `${CWindowWidth - 300}px`, margin: 'auto' }}>
            <div>
              <Title level={2}>채용 공고</Title>
              <div>정규직 공고, 인턴공고 중 {companyName} 관련 공고를 모아두었습니다.</div>
            </div>
            <List
              pagination={{
                ...pagination,
                total: get(data, 'total', 0),
                position: 'top',
                onChange: (page) => {
                  reloadPage({
                    current: page,
                    pageSize: pagination.pageSize,
                  });
                },
              }}
              loading={{ spinning: loading, delay: 1000 }}
              grid={{ gutter: 12, xs: 1, sm: 2, md: 2, lg: 3, xl: 4, xxl: 4 }}
              dataSource={get(data, 'contents')}
              renderItem={(item) => (
                <List.Item>
                  <>
                    {get(item, 'category') === ERecruitmentCategory.Fulltime ? (
                      <FulltimeRecruitmentCard showFulltime={true} data={item} />
                    ) : (
                      <ParttimeRecruitmentCard showIntern={true} data={item} />
                    )}
                  </>
                </List.Item>
              )}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default CompanySearch;
